
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { ShoppingCart, Tag, CreditCard, Gift } from 'lucide-react';
import LoadingSpinner from '@/components/masterlicense/LoadingSpinner';

interface GiftCardProduct {
  id: string;
  name: string;
  description: string;
  image: {
    url: string;
  };
  countries: string[];
  currencies: {
    [currencyCode: string]: {
      min: number;
      max: number;
    };
  };
  denomination_type: string;
}

export interface TremendousProductsResponse {
  products: GiftCardProduct[];
}

const TremendousGiftCardCatalog = () => {
  const [products, setProducts] = useState<GiftCardProduct[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const { toast } = useToast();

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('tremendous', {
        body: {}
      });

      if (error) {
        throw new Error(error.message);
      }
      
      console.log("Tremendous API response:", data);
      
      if (data?.products && Array.isArray(data.products)) {
        setProducts(data.products);
      } else {
        console.error("Invalid products response:", data);
        toast({
          title: "Error",
          description: "Failed to retrieve gift card catalog",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      toast({
        title: "Error",
        description: "Failed to retrieve gift card catalog",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSelectedProducts = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('tremendous', {
        body: { action: 'getSelectedProducts' }
      });

      if (error) {
        throw new Error(error.message);
      }
      
      if (data?.products && Array.isArray(data.products)) {
        setSelectedProducts(data.products);
      }
    } catch (error) {
      console.error("Error fetching selected products:", error);
    }
  };

  const toggleProductSelection = async (productId: string, selected: boolean) => {
    try {
      const { data, error } = await supabase.functions.invoke('tremendous', {
        body: { productId, selected, action: 'toggleProduct' }
      });

      if (error) {
        throw new Error(error.message);
      }
      
      if (data?.selectedProducts) {
        setSelectedProducts(data.selectedProducts);
        toast({
          title: `Gift Card ${selected ? 'Enabled' : 'Disabled'}`,
          description: `The gift card has been ${selected ? 'enabled' : 'disabled'} for rewards`,
          variant: "default"
        });
      }
    } catch (error) {
      console.error("Error toggling product selection:", error);
      toast({
        title: "Error",
        description: "Failed to update gift card selection",
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchSelectedProducts();
  }, []);

  const filteredProducts = products.filter(product => 
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">Tremendous Gift Card Catalog</h2>
          <p className="text-muted-foreground">
            Select which gift cards are available for rewards
          </p>
        </div>
        
        <div className="w-full md:w-64">
          <Input
            placeholder="Search gift cards..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>
      </div>

      {products.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center">
            <Gift className="mx-auto h-10 w-10 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No Gift Cards Available</h3>
            <p className="text-sm text-muted-foreground mt-2">
              There are no gift cards currently available from Tremendous.
            </p>
            <Button className="mt-4" onClick={fetchProducts}>
              Refresh Catalog
            </Button>
          </CardContent>
        </Card>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-lg">No gift cards match your search.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => (
            <Card key={product.id} className="overflow-hidden flex flex-col">
              <div className="aspect-[4/3] relative bg-muted">
                {product.image?.url ? (
                  <img 
                    src={product.image.url} 
                    alt={product.name} 
                    className="w-full h-full object-contain p-4"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://placehold.co/400x300?text=Gift+Card';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <ShoppingCart className="h-16 w-16 text-gray-400" />
                  </div>
                )}
              </div>
              
              <CardHeader>
                <CardTitle>{product.name}</CardTitle>
                <CardDescription className="line-clamp-2">
                  {product.description || "Gift card for online or in-store purchases"}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="flex-grow">
                <div className="space-y-2">
                  <div className="flex items-center text-sm">
                    <Tag className="h-4 w-4 mr-2" />
                    <span className="text-muted-foreground">
                      Type: {product.denomination_type === 'FIXED' ? 'Fixed Amount' : 'Variable Amount'}
                    </span>
                  </div>
                  <div className="flex items-center text-sm">
                    <CreditCard className="h-4 w-4 mr-2" />
                    {Object.entries(product.currencies).map(([currency, range]) => (
                      <span key={currency} className="text-muted-foreground">
                        {product.denomination_type === 'FIXED'
                          ? `${currency}: Fixed options available`
                          : `${currency}: ${range.min} - ${range.max}`}
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
              
              <CardFooter className="border-t p-4">
                <div className="flex items-center justify-between w-full">
                  <Label htmlFor={`enable-${product.id}`} className="cursor-pointer">
                    Enable for Rewards
                  </Label>
                  <Switch
                    id={`enable-${product.id}`}
                    checked={selectedProducts.includes(product.id)}
                    onCheckedChange={(checked) => toggleProductSelection(product.id, checked)}
                  />
                </div>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default TremendousGiftCardCatalog;
